/**
 * How it works — three friendly steps for pet owners. Numbered clay tokens
 * with a connecting dashed line on desktop.
 */
import { CalendarHeart, MapPin, Search, type LucideIcon } from 'lucide-react';

type Step = {
  icon: LucideIcon;
  title: string;
  body: string;
};

const STEPS: Step[] = [
  {
    icon: Search,
    title: 'Tell us about your pet',
    body: 'Add Bella once — breed, quirks and all. Every booking is tailored to her from then on.',
  },
  {
    icon: MapPin,
    title: 'Discover trusted pros',
    body: 'Browse verified vets, walkers, groomers and more near you, with real reviews and clear pricing.',
  },
  {
    icon: CalendarHeart,
    title: 'Book & relax',
    body: 'Pick a slot, pay securely, and get reminders. Rebook your favourites in a single tap.',
  },
];

export function LandingHowItWorks() {
  return (
    <section id="how-it-works" className="scroll-mt-24 bg-secondary/40 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border-2 border-brand-outline-variant/30 bg-card px-4 py-1.5 text-sm font-semibold shadow-clay-sm">
            For pet parents
          </span>
          <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Booking care has never been this easy
          </h2>
        </div>

        <div className="relative mt-14 grid gap-8 md:grid-cols-3">
          {/* Dashed connector (desktop) */}
          <div
            aria-hidden
            className="absolute left-0 right-0 top-7 hidden border-t-2 border-dashed border-brand-outline-variant/40 md:block"
          />
          {STEPS.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="relative flex flex-col items-center text-center">
              <div className="relative flex h-14 w-14 items-center justify-center rounded-full accent-gradient text-white shadow-clay">
                <Icon className="h-6 w-6" />
                <span className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-foreground font-heading text-sm font-bold text-background">
                  {i + 1}
                </span>
              </div>
              <h3 className="mt-5 font-heading text-xl font-bold">{title}</h3>
              <p className="mt-2 max-w-xs text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
